import type { MWParam } from '@/types/MW';
import { MWParams, LocalStorageKeys } from '@/utils/constants';

export type AppliedDiscount = {
  id: string;
  discountSequence: number;
  discountType: number;
  value: number;
};

export function applyDiscounts(unitPrice: number, discounts: AppliedDiscount[]) {
  const basePrice = unitPrice;
  if (!discounts || !basePrice) return;

  const sequences: Record<string, AppliedDiscount[]> = {};
  discounts.forEach((discount) => {
    if (!sequences[discount.discountSequence]) {
      sequences[discount.discountSequence] = [];
    }
    sequences[discount.discountSequence].push(discount);
  });

  let currentPrice = basePrice;

  Object.keys(sequences).forEach((sequence) => {
    const sequenceDiscounts = sequences[sequence];

    let sequenceTotalDiscount = 0;
    sequenceDiscounts.forEach((discount: any) => {
      let total = 0;
      if (discount.discountType === 0) {
        // %
        total = currentPrice * (discount.value / 100);
      } else if (discount.discountType === 1) {
        total = discount.value;
      }
      sequenceTotalDiscount += total;
    });
    currentPrice -= sequenceTotalDiscount;
    // negative values
    if (currentPrice < 0) currentPrice = 0;
  });

  return currentPrice;
}

export function stringifyAppliedDiscounts(pricingPolicyMatrixDetails: AppliedDiscount[]) {
  const stringifiedDiscounts = pricingPolicyMatrixDetails.length
    ? JSON.stringify(pricingPolicyMatrixDetails).slice(1, -1)
    : null;
  return stringifiedDiscounts;
}

let decimalPlaces = 2;
export function initializeDecimalPlaces() {
  const memoryParams = localStorage.getItem(LocalStorageKeys.MwParams);
  if (memoryParams) {
    const params = JSON.parse(memoryParams) as MWParam[];
    if (params && params.length > 0) {
      const decimalsParam = params.find((x) => x.code === MWParams.COMPUTE_DIGITS);
      const decimalsParamValue = !decimalsParam?.defaultValue ? '2' : decimalsParam?.defaultValue;
      decimalPlaces = Number(decimalsParamValue);
    }
  }
}

export function roundValue(value: number | undefined, decimals: number = decimalPlaces): number {
  const numericValue = value ?? 0;
  const factor = Math.pow(10, decimals);
  return Math.round(numericValue * factor) / factor;
}

export function computeAdditionalDiscountByPercentile(
  lineTotal: number,
  documentTotal: number | undefined,
  targetDocumentTotal: number,
  subtractVat: boolean = false,
  lineVatFactor: number = 0
): any {
  if (!documentTotal) return undefined;

  const percentile = roundValue((lineTotal / documentTotal) * 100);
  const difference = roundValue(documentTotal - targetDocumentTotal);

  if (difference == 0) return undefined;
  let additionalDiscountValue = roundValue((percentile / 100) * difference);

  if (subtractVat) {
    const vat = roundValue((lineVatFactor || 0) / 100);
    additionalDiscountValue = roundValue(additionalDiscountValue / (1 + vat));
  }

  return additionalDiscountValue;
}
